"use client"

import { Link, useLocation } from "react-router-dom"
import { ChevronRight, type LucideIcon } from "lucide-react"
import { toast } from "sonner"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from "@/components/ui/sidebar"
import { PATH_ROOT } from "@/routes/paths"
import { Inbox, Calendar, Search } from "lucide-react"
import { AriIcon, DashboardIcon } from "./icons"
import { useFeatureFlag, FeatureFlags } from "@/hooks/common/useFeatureFlag"
import type { PageFlagName } from "@/hooks/common/useFeatureFlag"

type SidebarSubItem = {
  label: string
  icon?: LucideIcon | React.ComponentType<React.SVGProps<SVGSVGElement>>
  useAvatar?: boolean
  url?: string
}

export type NavMainItem = {
  label: string
  icon: LucideIcon | React.ComponentType<React.SVGProps<SVGSVGElement>>
  url?: string
  flag?: PageFlagName
  subItems?: SidebarSubItem[]
  isActive?: boolean
}

type NavMainProps = {
  items: NavMainItem[]
  onSearchClick?: () => void
  isDemo?: boolean
}

/**
 * Renders a single nav item that may be gated by a feature flag.
 * When disabled: greyed out + toast on click. When loading: normal appearance.
 */
function FlaggedNavItem({ item, isActive }: { item: NavMainItem; isActive: boolean }) {
  const flagValue = useFeatureFlag(item.flag!);
  const isDisabled = flagValue === false;

  if (isDisabled) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip={item.label}
          className="opacity-40 cursor-not-allowed"
          onClick={(e) => {
            e.preventDefault()
            toast.info(`${item.label} is not available yet`)
          }}
        >
          <item.icon />
          <span>{item.label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  // Flag enabled or still loading — render normally
  return (
    <SidebarMenuItem>
      {item.url ? (
        <Link to={item.url}>
          <SidebarMenuButton tooltip={item.label} isActive={isActive}>
            <item.icon />
            <span>{item.label}</span>
          </SidebarMenuButton>
        </Link>
      ) : (
        <SidebarMenuButton tooltip={item.label} isActive={isActive}>
          <item.icon />
          <span>{item.label}</span>
        </SidebarMenuButton>
      )}
    </SidebarMenuItem>
  )
}

/** Ask Ari — chat home + create hub (replaces separate Chat / Create entries). */
function AskAriNavItem({ isDemo = false }: { isDemo?: boolean }) {
  const location = useLocation()
  const chatPath = isDemo ? PATH_ROOT.demo.chat : PATH_ROOT.main.chat
  const createPath = isDemo ? PATH_ROOT.demo.create : PATH_ROOT.main.create
  const isActive = location.pathname === chatPath || location.pathname === createPath

  return (
    <Link className="cursor-pointer" to={chatPath}>
      <SidebarMenuItem>
        <SidebarMenuButton tooltip="Ask Ari" isActive={isActive}>
          <AriIcon variant="gradient" aria-hidden="true" />
          <span>Ask Ari</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </Link>
  )
}

/** Dashboard link — gated by a feature flag. */
function DashboardNavItem({ isDemo = false }: { isDemo?: boolean }) {
  const location = useLocation()
  const dashboardPath = isDemo ? PATH_ROOT.demo.dashboard : PATH_ROOT.main.dashboard
  const dashboardFlag = isDemo ? FeatureFlags.DEMO_DASHBOARD : FeatureFlags.PROD_DASHBOARD
  const flagValue = useFeatureFlag(dashboardFlag);
  const isDisabled = flagValue === false;

  if (isDisabled) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip="Dashboard"
          className="opacity-40 cursor-not-allowed"
          onClick={(e) => {
            e.preventDefault()
            toast.info("Dashboard is not available yet")
          }}
        >
          <DashboardIcon />
          <span>Dashboard</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return (
    <Link className="cursor-pointer" to={dashboardPath}>
      <SidebarMenuItem>
        <SidebarMenuButton tooltip="Dashboard" isActive={location.pathname === dashboardPath}>
          <DashboardIcon />
          <span>Dashboard</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </Link>
  )
}

/** Inbox link — gated by a feature flag. */
function InboxNavItem({ isDemo = false }: { isDemo?: boolean }) {
  const location = useLocation()
  const inboxPath = isDemo ? PATH_ROOT.demo.inbox : PATH_ROOT.main.inbox
  const inboxFlag = isDemo ? FeatureFlags.DEMO_INBOX : FeatureFlags.PROD_INBOX
  const flagValue = useFeatureFlag(inboxFlag);
  const isDisabled = flagValue === false;

  if (isDisabled) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip="Inbox"
          className="opacity-40 cursor-not-allowed"
          onClick={(e) => {
            e.preventDefault()
            toast.info("Inbox is not available yet")
          }}
        >
          <Inbox />
          <span>Inbox</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return (
    <Link className="cursor-pointer" to={inboxPath}>
      <SidebarMenuItem>
        <SidebarMenuButton tooltip="Inbox" isActive={location.pathname === inboxPath}>
          <Inbox />
          <span>Inbox</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </Link>
  )
}

/** Calendar link — gated by a feature flag. */
function CalendarNavItem({ isDemo = false }: { isDemo?: boolean }) {
  const location = useLocation()
  const calendarPath = isDemo ? PATH_ROOT.demo.calendar : PATH_ROOT.main.calendar
  const calendarFlag = isDemo ? FeatureFlags.DEMO_CALENDAR : FeatureFlags.PROD_CALENDAR
  const flagValue = useFeatureFlag(calendarFlag);
  const isDisabled = flagValue === false;

  if (isDisabled) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          tooltip="Calendar"
          className="opacity-40 cursor-not-allowed"
          onClick={(e) => {
            e.preventDefault()
            toast.info("Calendar is not available yet")
          }}
        >
          <Calendar />
          <span>Calendar</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return (
    <Link className="cursor-pointer" to={calendarPath}>
      <SidebarMenuItem>
        <SidebarMenuButton tooltip="Calendar" isActive={location.pathname === calendarPath}>
          <Calendar />
          <span>Calendar</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </Link>
  )
}

const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)
const modKey = isMac ? "⌘" : "Ctrl"

/** Search item — triggers the command palette. */
function SearchNavItem({ onSearchClick }: { onSearchClick?: () => void }) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={onSearchClick}
        tooltip={`Search (${modKey}+K)`}
      >
        <Search />
        <span>Search</span>
        <kbd className="ml-auto pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">{modKey}</span>K
        </kbd>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

/** Items that are pinned to the top of the nav (rendered as dedicated components). */
const PINNED_LABELS = new Set(["Inbox", "Calendar", "Search"])

export function NavMain({ items, onSearchClick, isDemo = false }: NavMainProps) {
  const location = useLocation()

  // Filter out items that are rendered as dedicated components above the separator
  const remainingItems = items.filter((item) => !PINNED_LABELS.has(item.label))

  return (
    <>
      <SidebarGroup>
        <SidebarGroupLabel>Platform</SidebarGroupLabel>
        <SidebarMenu>
          {/* Primary nav: Chat, Dashboard, Inbox, Calendar, Search */}
          <AskAriNavItem isDemo={isDemo} />
          <DashboardNavItem isDemo={isDemo} />
          <InboxNavItem isDemo={isDemo} />
          <CalendarNavItem isDemo={isDemo} />
          <SearchNavItem onSearchClick={onSearchClick} />
        </SidebarMenu>
        <SidebarSeparator className="my-1.5 w-[calc(100%-1rem)]!" />
        <SidebarMenu>
          {remainingItems.map((item) => {
            const hasSubItems = item.subItems && item.subItems.length > 0
            if (!hasSubItems) {
              // Flagged items use their own component (calls the hook)
              if (item.flag) {
                return <FlaggedNavItem key={item.label} item={item} isActive={location.pathname === item.url} />
              }

              const content = (
                <SidebarMenuButton tooltip={item.label} isActive={location.pathname === item.url}>
                  <item.icon />
                  <span>{item.label}</span>
                </SidebarMenuButton>
              )
              return (
                <SidebarMenuItem key={item.label}>
                  {item.url ? (
                    <Link to={item.url}>{content}</Link>
                  ) : (
                    content
                  )}
                </SidebarMenuItem>
              )
            }

            return (
              <Collapsible
                key={item.label}
                defaultOpen={item.isActive}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger render={(props) => (
                    <SidebarMenuButton tooltip={item.label} {...props}>
                      <item.icon />
                      <span>{item.label}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      <SidebarMenuBadge className="bg-[#fff700] text-yellow-950 rounded-full">
                        {item.subItems?.length || 0}
                      </SidebarMenuBadge>
                    </SidebarMenuButton>
                  )} />
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.subItems!.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.label}>
                          <SidebarMenuSubButton asChild={!!subItem.url}>
                            {subItem.url ? (
                              <Link to={subItem.url}>
                                {subItem.icon ? (
                                  <subItem.icon />
                                ) : null}
                                <span>{subItem.label}</span>
                              </Link>
                            ) : (
                              <>
                                {subItem.icon ? (
                                  <subItem.icon />
                                ) : null}
                                <span>{subItem.label}</span>
                              </>
                            )}
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )
          })}
        </SidebarMenu>
      </SidebarGroup>
    </>
  )
}
